import React from 'react';
import { useSelector } from 'react-redux';

import { css } from 'config/emotionCss';
import { getCountries } from 'modules/countries/selectors';
import { getCountryTranslations } from 'modules/i18n/selectors/data';
import Label from 'ui/fields/PhoneField/Label';
import SelectItem from 'ui/fields/PhoneField/SelectItem';
import CountrySelect from 'ui/fields/selects/CountrySelect';
import { getSupportedCountryByPhoneNumber } from 'utils/PhoneNumberUtils';

import SearchInput from '../SearchInput';

const countrySelectDropdownStyle = css`
  width: 277px;
  padding: 12px;
  border-radius: 2px;
`;

/** @param {string} searchValue  */
const countryFilter =
  searchValue =>
    ({ label, name, code, alpha3, prefix }) => {
      if (searchValue) {
        const formattedSearchValue = searchValue.replace('+', '').toLowerCase();
        // label is the country translated in the app language
        // name is the country translated in the country's language
        return [label, name, code, alpha3, String(prefix)].some(searchField =>
          searchField?.toLowerCase().includes(formattedSearchValue));
      }
      return true;
    };

const CountryPhoneSelect = ({ value: phoneNumber, onChange, disabled, ...rest }) => {
  const inputRef = React.useRef();
  const menuRef = React.useRef();
  const countryCollection = useSelector(getCountries);
  const countryTranslations = useSelector(getCountryTranslations);
  const [searchValue, setSearchValue] = React.useState('');

  const { countries, supportedCountryCodes } = React.useMemo(
    () => Object.values(countryCollection).reduce(
      (acc, country) => {
        acc.countries.push(country);
        acc.supportedCountryCodes.push(country.code);
        return acc;
      },
      { countries: [], supportedCountryCodes: [] }
    ),
    [countryCollection]
  );

  const [selectedCountryCode, setSelectedCountryCode] = React.useState(countries.find(({ prefix }) => String(prefix) === phoneNumber.prefix)?.code);

  const selectedCountry = React.useMemo(
    () => {
      if (phoneNumber.local?.length > 0) {
        const country = getSupportedCountryByPhoneNumber(phoneNumber, supportedCountryCodes);
        return country ?? selectedCountryCode;
      }
      return selectedCountryCode;
    },
    [phoneNumber, supportedCountryCodes, selectedCountryCode]
  );

  const overriddenOptions = React.useMemo(
    () => countries.filter(country => countryFilter(searchValue)({
      ...country,
      label: countryTranslations[country.code]
    })).map(({ code }) => code),
    [countries, countryTranslations, searchValue]
  );

  const handleOnSearch = React.useCallback(({ target }) => {
    setSearchValue(target.value.trim());
  }, []);

  const getPopupContainer = React.useCallback(node => {
    let area = node;
    while (area && area.nodeName !== 'BODY') {
      if (area.classList.contains('ant-select')) {
        return area;
      }
      area = area.parentElement;
    }
    return document.body;
  }, []);

  /** RENDERS */
  const popupRender = React.useCallback(
    menu => (
      <>
        <SearchInput
          status="none"
          ref={inputRef}
          className="mb-1"
          value={searchValue}
          onChange={handleOnSearch}
          placeholder="page.fiche.form.client_information.search"
        />
        <div ref={menuRef}>
          {menu}
        </div>
      </>
    ),
    [handleOnSearch, searchValue]
  );

  const optionRender = React.useCallback(
    ({ data: { code: countryCode }, label }) => (
      countryCode ? <SelectItem countryCode={countryCode} /> : label
    ),
    []
  );

  const getCountryLabel = React.useCallback(
    ({ value: countryCode, label }) => (
      countryCode ? <Label countryCode={countryCode} /> : label
    ),
    []
  );

  const handleOpenChange = React.useCallback(isOpen => {
    setSearchValue('');
    if (isOpen) {
      setTimeout(() => {
        inputRef.current.focus();
        // TODO : remove this crap
        // was made 1h before release to avoid regression. SO SORRY.
        // shame shame shame
        requestAnimationFrame(() => {
          menuRef.current?.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        });
      }, 100);
    }
  }, []);

  const handleCountryOnChange = React.useCallback(
    (countryCode) => {
      setSelectedCountryCode(countryCode);
      onChange({
        ...phoneNumber,
        prefix: countryCollection[countryCode]?.prefix?.toString()
      });
    },
    [countryCollection, onChange, phoneNumber]
  );

  return (
    <CountrySelect
      {...rest}
      className="w-100"
      withSearch={false}
      disabled={disabled}
      value={selectedCountry}
      popupRender={popupRender}
      optionRender={optionRender}
      popupMatchSelectWidth={false}
      labelRender={getCountryLabel}
      data-cy="phoneNumber-country"
      onOpenChange={handleOpenChange}
      onChange={handleCountryOnChange}
      getPopupContainer={getPopupContainer}
      overriddenOptions={overriddenOptions}
      classNames={{
        popup: {
          root: countrySelectDropdownStyle,
        },
      }}
    />
  );
};

export default CountryPhoneSelect;
